import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronLeft, ChevronRight, Loader2, Wallet } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { HelpHint } from './HelpHint';
import { ScrollX } from './ScrollX';

/**
 * What each technician earned in field allowances this month.
 *
 * ## Why every job is shown, including the ones that paid nothing
 *
 * This is the number somebody is paid, and it will be disagreed with. A total
 * on its own can only be accepted or argued about; a total with the jobs under
 * it can be checked. A technician who counted six connections and was paid for
 * four can open the month, see the two marked over the daily limit, and see
 * which two they were — which is a conversation about the cap rather than
 * about whether the system is right.
 *
 * ## Why a missing quantity is loud
 *
 * A cable run with no length recorded pays nothing, and that is a form
 * somebody has to finish rather than a run of no length. It is counted
 * separately and coloured, because paying it as zero would look exactly like
 * work that was never done.
 */
export function AllowancesTable() {
  const [month, setMonth] = useState(thisMonth);
  const [openFor, setOpenFor] = useState<string | null>(null);

  const { data, isPending, isError } = useQuery({
    queryKey: ['allowances', month],
    queryFn: () => api.allowances(month),
  });

  const rows = data?.rows ?? [];
  const earning = rows.filter((row) => row.totalKes > 0 || row.needsQuantity > 0);
  const unfinished = rows.reduce((sum, row) => sum + row.needsQuantity, 0);

  return (
    <section className="card">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-1.5 font-heading text-lg">
          <Wallet className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          Field allowances
          <HelpHint>
            What each technician earned on top of salary for work they completed and recorded this
            month, at the rates set on each job type.
            <br />
            <br />
            Worked out from the jobs every time it is opened rather than stored, so correcting a
            wrongly recorded kilometre corrects the pay. Open a row to see every job that
            contributed and what it was worth.
          </HelpHint>
        </h3>

        <div className="flex items-center gap-1">
          <button
            type="button"
            className="btn-secondary px-2 py-1.5"
            onClick={() => setMonth(shift(month, -1))}
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <span className="min-w-[8.5rem] text-center text-sm font-medium tabular-nums">
            {monthName(month)}
          </span>
          {/*
            Nothing beyond this month. A future month is always empty, and an
            empty statement that looks like a finished one is the sort of thing
            somebody screenshots.
          */}
          <button
            type="button"
            className="btn-secondary px-2 py-1.5 disabled:opacity-40"
            onClick={() => setMonth(shift(month, 1))}
            disabled={month >= thisMonth()}
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      {isPending && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}

      {isError && (
        <p className="text-sm text-danger-700">
          Could not work out this month&rsquo;s allowances. The figures are calculated from the
          jobs, so nothing has been lost — try again.
        </p>
      )}

      {data && earning.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Nothing earned in {monthName(month)}. Allowances appear once a job type carries a rate —
          set those under Lists → Job types.
        </p>
      )}

      {data && earning.length > 0 && (
        <>
          <ScrollX>
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-2 font-medium">Technician</th>
                  <th className="py-2 text-right font-medium">Jobs paid</th>
                  <th className="py-2 text-right font-medium">
                    <span title="Jobs with no quantity recorded, which cannot be paid yet">
                      Waiting on a quantity
                    </span>
                  </th>
                  <th className="py-2 text-right font-medium">Earned</th>
                  <th className="py-2" />
                </tr>
              </thead>

              <tbody className="divide-y divide-border">
                {earning.map((row) => (
                  <Row
                    key={row.technicianId}
                    row={row}
                    month={month}
                    open={openFor === row.technicianId}
                    onToggle={() =>
                      setOpenFor(openFor === row.technicianId ? null : row.technicianId)
                    }
                  />
                ))}
              </tbody>

              <tfoot className="border-t-2 border-border">
                <tr>
                  <td className="py-2 font-semibold">Total</td>
                  <td />
                  <td className="py-2 text-right tabular-nums">
                    {unfinished > 0 && (
                      <span className="font-semibold text-accent-700">{unfinished}</span>
                    )}
                  </td>
                  <td className="py-2 text-right font-semibold tabular-nums">
                    {money(data.totalKes)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </ScrollX>

          {unfinished > 0 && (
            <p className="mt-3 flex items-start gap-1.5 text-xs text-accent-800">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {unfinished} {unfinished === 1 ? 'job has' : 'jobs have'} no quantity recorded and
              cannot be paid yet. Open the technician to see which, and ask them to complete the
              form — the allowance appears as soon as the number is there.
            </p>
          )}
        </>
      )}
    </section>
  );
}

function Row({
  row,
  month,
  open,
  onToggle,
}: {
  row: { technicianId: string; name: string; totalKes: number; jobs: number; needsQuantity: number };
  month: string;
  open: boolean;
  onToggle: () => void;
}) {
  /*
   * Loaded only when the row is opened. The summary already costs one query per
   * technician on the server; fetching every statement up front would make
   * opening the page pay for detail nobody has asked to see.
   */
  const statement = useQuery({
    queryKey: ['allowance-statement', row.technicianId, month],
    queryFn: () => api.allowanceStatement(row.technicianId, month),
    enabled: open,
  });

  return (
    <>
      <tr>
        <td className="py-2 font-medium">{row.name}</td>
        <td className="py-2 text-right tabular-nums">{row.jobs}</td>
        <td className="py-2 text-right tabular-nums">
          {row.needsQuantity > 0 ? (
            <span className="font-semibold text-accent-700">{row.needsQuantity}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
        <td className="py-2 text-right font-medium tabular-nums">{money(row.totalKes)}</td>
        <td className="py-2 text-right">
          <button
            type="button"
            className="btn-secondary px-2 py-1 text-xs"
            onClick={onToggle}
            aria-expanded={open}
          >
            <ChevronDown
              className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
            {open ? 'Hide' : 'Show'} jobs
          </button>
        </td>
      </tr>

      {open && (
        <tr>
          <td colSpan={5} className="bg-muted/30 px-3 py-3">
            {statement.isPending && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}

            {statement.isError && (
              <p className="text-sm text-danger-700">Could not load the jobs behind this figure.</p>
            )}

            {statement.data && statement.data.lines.length === 0 && (
              <p className="text-sm text-muted-foreground">No finished jobs in this month.</p>
            )}

            {statement.data && statement.data.lines.length > 0 && (
              <table className="w-full text-xs">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="py-1 font-medium">Day</th>
                    <th className="py-1 font-medium">Ticket</th>
                    <th className="py-1 font-medium">Work</th>
                    <th className="py-1 text-right font-medium">Quantity</th>
                    <th className="py-1 text-right font-medium">Pays</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {statement.data.lines.map((line) => (
                    <tr key={line.jobId} className={line.excluded ? 'text-muted-foreground' : ''}>
                      <td className="py-1 tabular-nums">{line.day.slice(8)}</td>
                      <td className="py-1">{line.reference}</td>
                      <td className="py-1">
                        {line.jobTypeName}
                        {line.excluded && (
                          <span
                            className={`ml-2 badge ${
                              line.excluded === 'no quantity recorded'
                                ? 'bg-accent-50 text-accent-800'
                                : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            {line.excluded}
                          </span>
                        )}
                      </td>
                      <td className="py-1 text-right tabular-nums">
                        {line.quantity === null ? '—' : `${line.quantity} ${line.unitLabel ?? ''}`}
                      </td>
                      <td className="py-1 text-right tabular-nums">
                        {line.kes > 0 ? money(line.kes) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/** Whole shillings, grouped. Nobody is paid a fraction of one. */
function money(kes: number): string {
  return `KES ${kes.toLocaleString('en-KE')}`;
}

/** The current month as `YYYY-MM`, in the reader's own calendar. */
function thisMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Step a `YYYY-MM` by whole months.
 *
 * Built by hand rather than by adding to a Date, because `setMonth` on the
 * 31st lands in the month after next — and a "previous month" button that
 * skips March is the kind of bug nobody reports, they just stop trusting the
 * screen.
 */
export function shift(month: string, by: number): string {
  const [year, mon] = month.split('-').map(Number);
  const index = (year ?? 0) * 12 + (mon ?? 1) - 1 + by;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
}

function monthName(month: string): string {
  const [year, mon] = month.split('-').map(Number);
  return new Date(Date.UTC(year ?? 0, (mon ?? 1) - 1, 1)).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
